import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../../../../../components/ui/Button';
import { Modal } from '../../../../../components/ui/Modal';
import { AddressFields, validateAddress } from '../../../../../components/address/AddressFields';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { COUNTRY_ISO2 } from '../../../../../features/geo/geoConstants';
import { EDITABLE_SHIPPING_METHODS } from '../../../../../features/orderDetails/orderConstants';
import {
  fetchDrivers,
  fetchOrderWarehouses,
  updateOrderShipping,
} from '../../../../../features/orderDetails/orderDetailsThunks';
import { selectIsCheckoutPlan } from '../../../../../features/auth/authSelectors';

const inputClass =
  'w-full bg-ninja-gray/50 dark:bg-[#111] border border-border rounded-xl px-4 py-2.5 text-sm text-ninja-dark dark:text-white outline-none focus:border-ninja-green transition-colors disabled:opacity-60';

const labelClass = 'block text-sm font-semibold text-ninja-dark dark:text-white mb-1.5';

const Field = ({ label, error, children }) => (
  <div>
    <label className={labelClass}>{label}</label>
    {children}
    {error && <p className="text-danger text-xs mt-1">{error}</p>}
  </div>
);

const EditShippingDialog = ({ onClose, order, onError }) => {
  const dispatch = useDispatch();

  const countryIso2 = order.seller?.company?.country_fk?.iso2 || COUNTRY_ISO2.CL;
  const countryId = order.seller?.company?.country_fk?.id;
  const isMexico = countryIso2 === COUNTRY_ISO2.MX;
  const companyId = order.seller?.company?.id;

  const isCheckoutPlan = useSelector(selectIsCheckoutPlan);

  const { warehouseOptions, loadingWarehouses, driverOptions } = useSelector(
    (state) => state.orderDetails
  );

  // Read from the order once, at mount: cancelling and reopening discards
  // edits, and a refetch while it is open does not wipe them.
  const [form, setForm] = useState(() => {
    const address = order.delivery?.shipping_address || {};
    return {
      shippingMethod: order.delivery?.shipping_method || '',
      warehouseId: order.warehouse?.id || '',
      driverId: order.delivery?.driver?.id || '',
      street: address.street || '',
      streetNumber: address.street_number || '',
      region: address.region || '',
      commune: address.commune || '',
      settlementId: '',
      settlement: address.settlement || '',
      postalCode: address.postal_code || '',
      notes: order.notes || '',
      dispatchGuide: order.delivery?.dispatch_guide_number || '',
      packages: order.delivery?.packages || '1',
      weight: order.delivery?.weight || '',
    };
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // The warehouses and drivers are fetched when the modal opens, not with the
  // page: paying for them while only reading an order would be wasteful.
  // `AddressFields` loads the geo tree on its own for the same reason.
  useEffect(() => {
    if (companyId) dispatch(fetchOrderWarehouses(companyId));
    if (isCheckoutPlan) dispatch(fetchDrivers());
  }, [companyId, isCheckoutPlan, dispatch]);

  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  /** `AddressFields` sends only what changed, cascade included. */
  const handleAddressChange = (next) => {
    setForm((prev) => ({ ...prev, ...next }));
    setErrors((prev) => {
      const cleared = { ...prev };
      Object.keys(next).forEach((key) => { cleared[key] = undefined; });
      // The colonia's error is keyed by its select, not by the stored name.
      if ('settlement' in next) cleared.settlementId = undefined;
      return cleared;
    });
  };

  const validate = () => {
    const found = validateAddress(form, { isMexico });

    if (!form.shippingMethod) found.shippingMethod = 'Campo obligatorio';
    if (!form.warehouseId) found.warehouseId = 'Campo obligatorio';
    if (!Number(form.packages) || Number(form.packages) < 1) {
      found.packages = 'Debe ser mínimo 1 bulto';
    }
    if (!Number(form.weight) || Number(form.weight) < 1) {
      found.weight = 'Debe ser mínimo 1 gramo';
    }

    setErrors(found);
    return Object.keys(found).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await dispatch(
        updateOrderShipping({ orderId: order.id, values: form, countryId, isMexico })
      ).unwrap();
      onClose();
    } catch (error) {
      onError(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`Editar Información de Entrega (${countryIso2})`}
      size="lg"
      busy={saving}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-h-[60vh] overflow-y-auto pr-1">
        <div>
          <FilterableSelect
            variant="form"
            label="Tipo de Envío"
            value={form.shippingMethod}
            onChange={(value) => setField('shippingMethod', value)}
            options={EDITABLE_SHIPPING_METHODS}
            hasError={Boolean(errors.shippingMethod)}
            placeholder="Seleccionar..."
            searchPlaceholder="Buscar tipo de envío..."
          />
          {errors.shippingMethod && (
            <p className="text-danger text-xs mt-1">{errors.shippingMethod}</p>
          )}
        </div>

        <div>
          <FilterableSelect
            variant="form"
            label="Bodega Origen"
            value={form.warehouseId}
            onChange={(value) => setField('warehouseId', value)}
            options={warehouseOptions}
            disabled={loadingWarehouses && warehouseOptions.length === 0}
            hasError={Boolean(errors.warehouseId)}
            placeholder={loadingWarehouses ? 'Cargando...' : 'Seleccionar...'}
            searchPlaceholder="Buscar bodega..."
          />
          {errors.warehouseId && <p className="text-danger text-xs mt-1">{errors.warehouseId}</p>}
        </div>

        {isCheckoutPlan && (
          <div className="sm:col-span-2">
            <FilterableSelect
              variant="form"
              label="Despachador"
              value={form.driverId}
              onChange={(value) => setField('driverId', value)}
              options={driverOptions}
              placeholder="Sin conductor asignado"
              searchPlaceholder="Buscar conductor..."
            />
          </div>
        )}

        <AddressFields
          country={countryIso2}
          value={form}
          onChange={handleAddressChange}
          errors={errors}
        />

        <div className="sm:col-span-2">
          <Field label="Notas">
            <input
              value={form.notes}
              onChange={(e) => setField('notes', e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>

        <div className="sm:col-span-2">
          <Field label="Guía de Despacho">
            <input
              data-testid="order-shipping-dispatch-guide"
              value={form.dispatchGuide}
              onChange={(e) => setField('dispatchGuide', e.target.value)}
              className={inputClass}
            />
          </Field>
        </div>

        <Field label="Bultos" error={errors.packages}>
          <input
            type="number"
            min="1"
            value={form.packages}
            onChange={(e) => setField('packages', e.target.value)}
            className={inputClass}
          />
        </Field>

        <Field label="Peso (gramos)" error={errors.weight}>
          <input
            type="number"
            min="1"
            value={form.weight}
            onChange={(e) => setField('weight', e.target.value)}
            className={inputClass}
          />
        </Field>
      </div>

      <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-border">
        <Button
          variant="subtle"
          pill
          onClick={onClose}
          disabled={saving}
        >
          Cancelar
        </Button>
        <Button
          variant="primary"
          pill
          onClick={handleSave}
          loading={saving}
          loadingLabel="Guardando..."
        >
          Guardar
        </Button>
      </div>
    </Modal>
  );
};

/**
 * Editor for the shipping block of an order.
 *
 * The address is not typed free-hand: it is rebuilt through `AddressFields`,
 * the same component the manual upload form uses, so an order cannot be edited
 * into an address the platform would have refused to create — and the two
 * screens cannot drift apart in their copy or their validation.
 *
 * Mounted only while open: each opening starts from the order as it is then,
 * and nothing that arrives while it is open overwrites the form.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.order
 * @param {(message: string) => void} props.onError
 */
const EditShippingModal = (props) => (props.isOpen ? <EditShippingDialog {...props} /> : null);

export default EditShippingModal;
